import React, { Component } from 'react'
import { observer } from 'mobx-react'
import { Trans, t } from '@lingui/macro'
import { observable } from 'mobx'
import { ButtonPrimary, ButtonLight } from 'front-end/src/components'

import getTopicVersion from './operations/getTopicVersion'
import getScormCourseStatus from './operations/getScormCourseStatus'
import getUploadCredentials from './operations/getUploadCredentials'
import importCourse from './operations/importCourse'
import patchTopicVersion from './operations/patchTopicVersion'
import Dropzone from 'react-dropzone'
import { Line } from 'rc-progress'
import { IScormCourse, ScormStatusEnum } from 'types/ScormCourse'
import { get } from 'lodash'
import zip from 'public/assets/img/file-icon-zip.svg'
import Table, {
  TableBody,
  TableRow,
  TableCol
} from '../../components/common/table/Table'
import './ManageScormPage.less'
import Spinner from '../../components/common/spinner/Spinner'
import bytes from 'bytes'
import { notification } from '../../components/common/notifications/Notifications'
import getAWSSignatureV4Form from '../../utils/getAWSSignatureV4Form'
import axios from 'axios'
import IconEmptyResources from 'public/assets/img/empty-resources.svg'

import { ManageScormPageTextElement } from './ManageScormPageTextElement'
import { TopicModelAsEditor } from 'front-end/src/graphql/private/PrivateSchemaTypes'

export interface IScormPageProps {
  topic: TopicModelAsEditor
  fetchTopic: () => void
}

const maxFileSize = bytes('1000MB')

// TODO refactor to functional component
@observer
class ManageScormPage extends Component<IScormPageProps> {
  @observable
  data = {}
  @observable
  isUploading = false
  @observable
  progress: number | null = null
  @observable
  progressText: string | null = null
  @observable
  fileSize: number | null = null
  @observable
  fileDetails: string | null = null
  @observable
  scormCourse: IScormCourse = {}
  @observable
  courseStatusObserver: ZenObservable.Subscription | null = null
  @observable
  title: string | null = null
  @observable
  summary: string | null = null
  @observable
  canPublish = false
  @observable
  workspaceId: number | null = null

  async fetchData() {
    this.workspaceId = this.props.topic.workspace_id

    const { title, summary, scormCourse } = await getTopicVersion({
      topicId: this.props.topic.id
    })
    this.title = title
    this.summary = summary || null
    this.scormCourse = scormCourse || {}

    if (this.scormCourse.status === ScormStatusEnum.RUNNING) {
      this.handleScormCourseStatus()
    }
  }

  componentDidMount() {
    this.fetchData()
  }

  componentWillUnmount() {
    if (this.courseStatusObserver) {
      this.courseStatusObserver.unsubscribe()
    }
  }

  handleProgress = async (progressEvent) => {
    const percentCompleted = Math.round(
      (progressEvent.loaded * 100) / progressEvent.total
    )
    this.isUploading = true
    this.progress = percentCompleted
    this.progressText = t`Uploading ${this.progress}%`

    if (percentCompleted === 100) {
      this.fileSize = Math.round(progressEvent.total / Math.pow(1024, 1))
    }
  }

  handleFileUpload = async (e) => {
    const file = e[0].name
    const data = await getUploadCredentials({
      topicId: this.props.topic.id,
      filename: file
    })

    const options = {
      headers: {
        'Content-Type': file.type
      },
      withCredentials: false,
      onUploadProgress: this.handleProgress
    }
    const { params } = data
    const fd = getAWSSignatureV4Form(params, e[0])

    await axios.post(data.uploadUrl, fd, options)

    try {
      await importCourse({
        topicId: this.props.topic.id,
        scormInput: {
          url: data.uploadUrl + file,
          filename: data.filename,
          filesize: this.fileSize || 0
        }
      })

      this.progressText = t`Done`
      this.handleScormCourseStatus()
    } finally {
      this.isUploading = false
      this.progress = 0
    }
  }

  handleScormCourseStatus = () => {
    const courseStatusGql = getScormCourseStatus({
      topicId: this.props.topic.id
    })
    this.courseStatusObserver = courseStatusGql.subscribe({
      next: (res) => {
        const { errors, data } = res
        if (data) {
          const { courseStatus } = get(
            res,
            'data.topic.draftVersion.scormCourse'
          )
          if (
            courseStatus.status === ScormStatusEnum.COMPLETE ||
            courseStatus.status === ScormStatusEnum.ERROR
          ) {
            courseStatusGql.stopPolling()
            this.courseStatusObserver && this.courseStatusObserver.unsubscribe()
          }
          this.props.fetchTopic()
          this.scormCourse = courseStatus
        }
        if (errors) {
          courseStatusGql.stopPolling()
          this.courseStatusObserver && this.courseStatusObserver.unsubscribe()
          throw errors
        }
      },
      error: (error) => {
        courseStatusGql.stopPolling()
        this.courseStatusObserver && this.courseStatusObserver.unsubscribe()
        throw error
      }
    })
  }

  renderDropZone() {
    return (
      <div className="ManageScormPage__upload">
        <Dropzone
          onDropAccepted={this.handleFileUpload}
          multiple={false}
          maxSize={maxFileSize}
          accept={'.zip'}
          onDropRejected={(fileRejections) => {
            const [{ file }] = fileRejections

            if (file.size > maxFileSize) {
              notification.error(
                t`Sorry, that file is ${bytes(
                  file.size
                )}, the upper limit for the file size is ${bytes(maxFileSize)}`
              )
            } else {
              notification.error(t`Sorry, that file type is not supported`)
            }
          }}
        >
          {({ getRootProps, getInputProps, open }) => (
            <div>
              <div className="ManageScormPage__dropzone" {...getRootProps()}>
                <h3>
                  <Trans>
                    Upload your SCORM, AICC, xAPI, cmi5 or LTI content package
                  </Trans>
                </h3>
                <p>
                  <Trans>Drag and drop your .zip file here</Trans>
                </p>
                <img src={IconEmptyResources} alt={t`Empty resource`} />
              </div>
              <div
                style={{
                  marginTop: '10px'
                }}
              >
                <input {...getInputProps()} />
                <ButtonPrimary
                  onClick={() => {
                    open()
                  }}
                >
                  <Trans>Find file on your computer</Trans>
                </ButtonPrimary>
              </div>
              <p
                style={{
                  marginTop: '10px'
                }}
              >
                <Trans>
                  {' '}
                  File must be zipped (.zip) SCORM, AICC, xAPI, cmi5 or LTI
                  compliant
                  <br />
                  Maximum package size: 1 GB
                </Trans>
              </p>
            </div>
          )}
        </Dropzone>
      </div>
    )
  }

  renderProgress() {
    return (
      <div className="ManageScormPage__progress">
        <p>{this.progressText}</p>
        <Line
          percent={this.progress || undefined}
          strokeWidth={2}
          strokeColor="#00bb77"
          trailWidth={2}
          trailColor="#ecf0f1"
        />
      </div>
    )
  }

  renderStatus() {
    const { status, job_details, file_details } = this.scormCourse

    let course
    if (job_details && job_details.course) {
      course = job_details.course
    }

    return (
      <div className="ManageScormPage__details">
        <div className="file-icon">
          <img src={zip} alt={zip} />
        </div>
        <div className="file-details">
          <h2>
            <Trans>Course package details</Trans>
          </h2>
          {course && (
            <p>
              <Trans>
                To preview the course click on the Preview option in the top
                right of this page.
              </Trans>
            </p>
          )}
          {status && file_details && job_details && (
            <Table>
              <TableBody>
                {course && (
                  <TableRow>
                    <TableCol>
                      <Trans>Course title</Trans>:
                    </TableCol>
                    <TableCol>{course.title}</TableCol>
                  </TableRow>
                )}
                {course && (
                  <TableRow>
                    <TableCol>
                      <Trans>Version</Trans>:
                    </TableCol>
                    <TableCol>{course.version + 1}</TableCol>
                  </TableRow>
                )}
                {course && (
                  <TableRow>
                    <TableCol>
                      <Trans>Learning standard</Trans>:
                    </TableCol>
                    <TableCol>{course.courseLearningStandard}</TableCol>
                  </TableRow>
                )}
                <TableRow>
                  <TableCol>
                    <Trans>Package file name</Trans>:
                  </TableCol>
                  <TableCol>{file_details.filename}</TableCol>
                </TableRow>
                <TableRow>
                  <TableCol>
                    <Trans>Package file size</Trans>:
                  </TableCol>
                  <TableCol>
                    {file_details.filesize}
                    Kb
                  </TableCol>
                </TableRow>
                {status === 'RUNNING' && (
                  <TableRow>
                    <TableCol colSpan={2}>
                      <Spinner />
                    </TableCol>
                  </TableRow>
                )}
                {status !== 'RUNNING' && job_details.status && (
                  <TableRow>
                    <TableCol colSpan={2}>
                      <ButtonLight
                        onClick={() => {
                          this.scormCourse = {}
                          console.log('Upload new version')
                        }}
                      >
                        <Trans>Upload new version</Trans>
                      </ButtonLight>
                    </TableCol>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    )
  }

  handleTextAreaSave = async () => {
    await patchTopicVersion({
      topicId: this.props.topic.id,
      title: this.title || '',
      summary: this.summary || ''
    })
    this.props.fetchTopic()
  }

  render() {
    return (
      <div className="ManageScormPage">
        <div className="ManageScormPage__topic-title">
          <textarea
            name="title"
            placeholder={t`Untitled resource`}
            value={this.title || ''}
            onBlur={this.handleTextAreaSave}
            onChange={(e) => {
              this.title = e.target.value
            }}
          />
        </div>
        <div className="ManageScormPage__topic-summary">
          <textarea
            id="title_summary"
            name="summary"
            placeholder={t`Add a short summary...`}
            value={this.summary || ''}
            onBlur={this.handleTextAreaSave}
            onChange={(e) => {
              this.summary = e.target.value
            }}
          />
        </div>
        <ManageScormPageTextElement {...this.props} />

        {!this.isUploading && !this.scormCourse.status && this.renderDropZone()}
        {this.isUploading && this.renderProgress()}
        {!this.isUploading && this.scormCourse.status && this.renderStatus()}
      </div>
    )
  }
}

export default ManageScormPage
